import { useEffect, useMemo, useRef, useState } from "react";

/* Dark menu styling to match the regular react-select dropdown menus */
const styles = `
.mg-price-panel {
  padding: 4px 0 0;
}

.mg-price-tiers {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: #111;
  border: 1px solid #333;
  border-radius: 6px;
  overflow: hidden;
}

.mg-price-tier-btn {
  width: 100%;
  min-height: 32px;
  padding: 8px 12px;
  border: none;
  border-radius: 0;
  background: #111;
  color: #fff;
  font-size: inherit;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background 0.15s ease;
}

.mg-price-tier-btn:hover,
.mg-price-tier-btn:focus-visible {
  background: #222;
}

.mg-price-tier-btn span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mg-price-tier-btn.mg-price-tier-active {
  background: #111;
  color: #fff;
}

.mg-price-tier-badge {
  font-size: 12px;
  font-weight: 700;
  padding-left: 8px;
  color: #3fa9f5;
}
`;

export default function PriceSlider({ tiers = [], selected, onChange }) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const [panelMaxH, setPanelMaxH] = useState(0);

  const allIds = useMemo(() => tiers.map((t) => t.id), [tiers]);

  // selected = array of tier ids; when undefined, treat as "all"
  const explicit = Array.isArray(selected) ? selected : allIds;
  const isAll = explicit.length === allIds.length;
  const isNone = explicit.length === 0;

  useEffect(() => {
    if (panelRef.current) {
      setPanelMaxH(open ? panelRef.current.scrollHeight : 0);
    }
  }, [open, tiers.length, explicit.length]);

  const emit = (ids) => {
    onChange?.(ids);
  };

  const toggleAll = () => {
    if (isAll) {
      // Unselect all -> no tiers => 0 games; Reset Games will bring it back to ALL
      emit([]);
    } else {
      // Select all tiers
      emit(allIds);
    }
  };

    const toggleTier = (id) => {
    // If we're in the "All Prices" state, first click on a tier
    // should *start a new selection* with only that tier.
    if (isAll) {
      emit([id]);
      return;
    }

    // After that, behave like a normal multi-select toggle.
    const has = explicit.includes(id);
    const next = has ? explicit.filter((x) => x !== id) : [...explicit, id];
    emit(next);
  };


  const pillSummary = useMemo(() => {
    if (isAll) return "Price";
    if (isNone) return "Price";
    if (explicit.length === 1) {
      const t = tiers.find((x) => x.id === explicit[0]);
      return t ? `Price: ${t.label}` : "Price";
    }
    return `Price: ${explicit.length} tiers`;
  }, [isAll, isNone, explicit, tiers]);

  const pillStyle = {
    width: "100%",
    minHeight: 36,
    padding: "8px 12px",
    boxSizing: "border-box",
    background: "#050505",
    border: `1px solid ${open ? "#666" : "#444"}`,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    color: "rgba(187,187,187,0.75)",
    fontWeight: "normal",
    cursor: "pointer",
  };

  return (
    <>
      <style>{styles}</style>

      {/* Accordion pill */}
      <button
        type="button"
        style={pillStyle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span
          style={{
            flex: 1,
            textAlign: "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {pillSummary}
        </span>

        {/* Divider */}
        <span
          aria-hidden
          style={{
            width: 1.5,
            height: 22,
            background: "#bbb",
            borderRadius: 1,
            margin: "0 8px",
          }}
        />

        {/* Caret (matches react-select dropdown indicator size) */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          style={{
            transform: `rotate(${open ? 180 : 0}deg)`,
            transition: "150ms",
            color: "#aaa",
            flexShrink: 0,
          }}
        >
          <polyline
            points="6 9 12 15 18 9"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          overflow: "hidden",
          maxHeight: open ? panelMaxH : 0,
          opacity: open ? 1 : 0,
          transition: "max-height 220ms ease, opacity 160ms ease",
        }}
        aria-hidden={!open}
      >
        <div className="mg-price-panel">
          <div className="mg-price-tiers">
            {/* All / Unselect all */}
            <button
              type="button"
              className={
                "mg-price-tier-btn" + (isAll ? " mg-price-tier-active" : "")
              }
              onClick={toggleAll}
            >
              <span>All Prices</span>
              <span className="mg-price-tier-badge">
                {isAll ? "✓" : isNone ? "•" : ""}
              </span>
            </button>

            {/* Individual tiers */}
            {tiers.map((tier) => {
              const active = explicit.includes(tier.id) || (isAll && !isNone);
              return (
                <button
                  key={tier.id}
                  type="button"
                  className={
                    "mg-price-tier-btn" +
                    (active ? " mg-price-tier-active" : "")
                  }
                  onClick={() => toggleTier(tier.id)}
                >
                  <span>{tier.label}</span>
                  {active && <span className="mg-price-tier-badge">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
